import { SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { has, isEmpty, isUndefined } from 'lodash-es'
import { ConnectedBuyFormatCurrency } from '@buy/connectedComponents'
import { BoxWrapper } from '@common/components'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingPeriod } from '@common/constants'
import { getPrice } from '@buy/utils/volume-components-helpers'
import { BillingTenureValue } from '../BillingPeriodSwitch'
import styles from '../../connectedComponents/Customize/customize.module.css'

interface BaseVolumeSectionProps {
    skuSelectors: Array<SKUSelectorsResponse>
    selectedPlanSku: string
    shortName: string
    isAnnual: boolean
    isMobileView: boolean | undefined
    userLabel: string
    yearLabel: string
    monthLabel: string
    loggedInNoUpgrade: boolean
    billingTenure: BillingTenureValue
    baseLine: OrderSummaryLine
}

interface VolumeContent {
    label: string
    value: number | undefined
}

export const BasePlanSection: FunctionComponent<BaseVolumeSectionProps> = ({
    skuSelectors,
    isMobileView,
    isAnnual,
    userLabel,
    yearLabel,
    monthLabel,
    loggedInNoUpgrade,
    billingTenure,
    baseLine,
}) => {
    const [content, setContent] = useState<Array<VolumeContent>>([])
    const [logo, setLogo] = useState<string>('')
    const [description, setDescription] = useState<string>('')
    const [title, setTitle] = useState<string>('')
    const [calculatedBasePrice, setCalculatedBasePrice] = useState(0)

    useEffect(() => {
        const planPriceIndex =
            isAnnual && isUndefined(billingTenure.leftOption) ? BillingPeriod.ANNUAL : billingTenure.leftOption!
        const price = getPrice(1, baseLine.allPrices[planPriceIndex]) * baseLine.multiplier * (isAnnual ? 12 : 1)
        setCalculatedBasePrice(price)
    }, [baseLine, billingTenure, isAnnual])

    useEffect(() => {
        if (!isEmpty(skuSelectors)) {
            const basePlanSku = skuSelectors.find((item) => has(item, 'includedInSku'))
            if (!isEmpty(basePlanSku)) {
                const data: Array<VolumeContent> = basePlanSku.includedInSku!.map((item) => ({
                    label: item.quantityDisplayLabel,
                    value: item.quantity,
                }))
                setContent([...data])
                setLogo(basePlanSku.icon ?? '')
                setDescription(basePlanSku.description ?? '')
                setTitle(basePlanSku.title ?? '')
            }
        }
    }, [skuSelectors])

    return (
        <BoxWrapper
            data-qat="customize-plan-header"
            {...(loggedInNoUpgrade && { modifier: styles['disabled-section'] })}
        >
            <div className={styles['flex-header']}>
                <div>
                    <div className={styles.label}>
                        {!isEmpty(logo) && <img src={logo} alt="logo" className={styles.logo} />}
                        <h3>{title}</h3>
                    </div>
                    <span className={styles.labelDescription}>{description}</span>
                </div>
            </div>
            <div className={styles['plan-summary']}>
                {isMobileView && (
                    <>
                        <ConnectedBuyFormatCurrency value={calculatedBasePrice} />{' '}
                        <span className={styles['plan-summary-base-price']}>/{userLabel}</span>
                        <span className={styles['plan-summary-base-price']}>/{isAnnual ? yearLabel : monthLabel}</span>
                    </>
                )}
                {content.map((item) => (
                    <p key={item.label} className={styles['plan-summary-row']} data-qat="buy-included-agents">
                        {item.label.replace('{0}', item.value!.toString())}
                    </p>
                ))}
            </div>
        </BoxWrapper>
    )
}
